import React from "react";
import { SimpleModulePage } from "./SimpleModulePage";
import { useAuth } from "../context/AuthContext";
import { MedicationModal } from "../components/MedicationModal";
import { config } from "../config/environment";
import { formatters } from "../utils/formatters";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = React.memo(({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
));

const getMealRelationLabel = (mealRelation: string | undefined): string => {
  if (!mealRelation) return "";
  const labels: Record<string, string> = {
    before_meal: "식전",
    with_meal: "식중",
    after_meal: "식후",
    between_meals: "공복",
    anytime: "상관없음",
  };
  return labels[mealRelation] || mealRelation;
};

interface MedsPageProps {
  onBack: () => void;
}

export const MedsPage: React.FC<MedsPageProps> = React.memo(({ onBack }) => {
  const { todayMeds, isLoggedIn, refreshTodayMeds } = useAuth();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  
  // ⚠️ 로컬 날짜 기준 (KST) - toISOString() 사용 금지
  const [selectedDate, setSelectedDate] = React.useState<string>(formatters.getLocalDateString());
  const [medsData, setMedsData] = React.useState(todayMeds);

  const [showTakenConfirmModal, setShowTakenConfirmModal] = React.useState(false);
  const [selectedMedForConfirm, setSelectedMedForConfirm] = React.useState<any>(null);
  const [selectedScheduleTime, setSelectedScheduleTime] = React.useState<any>(null);
  const [isSubmittingTaken, setIsSubmittingTaken] = React.useState(false);

  // 페이지 진입 시 스크롤 상단으로 초기화
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    setMedsData(todayMeds);
  }, [todayMeds]);

  // ⚠️ 로컬 날짜 기준 (KST) - toISOString() 사용 금지
  const today = formatters.getLocalDateString();
  const isToday = selectedDate === today;

  const handleDateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setIsRefreshing(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/meds/date/${newDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMedsData(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch meds for date:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenTakenConfirm = (med: any, schedTime: any) => {
    setSelectedMedForConfirm(med);
    setSelectedScheduleTime(schedTime);
    setShowTakenConfirmModal(true);
  };

  const handleConfirmTaken = async () => {
    if (!selectedMedForConfirm || !selectedScheduleTime) return;

    setIsSubmittingTaken(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('인증 토큰이 없습니다.');

      const now = new Date();
      const actualTime = `${selectedDate}T${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.000Z`;
      
      const response = await fetch(`${config.apiUrl}/meds/log/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: selectedMedForConfirm.scheduleId,
          reminder_type: selectedScheduleTime.type,
          reminder_date: selectedDate,
          status: 'taken',
          actual_datetime: actualTime,
        }),
      });

      if (response.ok) {
        setShowTakenConfirmModal(false);
        setSelectedMedForConfirm(null);
        setSelectedScheduleTime(null);
        
        // ⚠️ 로컬 날짜 기준 (KST) - toISOString() 사용 금지
        if (selectedDate === formatters.getLocalDateString()) {
          await refreshTodayMeds();
        } else {
          const refreshResponse = await fetch(`${config.apiUrl}/meds/date/${selectedDate}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            if (refreshData.success) {
              setMedsData(refreshData);
            }
          }
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || '복용 기록 저장 실패');
      }
    } catch (error) {
      console.error('Failed to record medication taken:', error);
      alert('복용 기록 저장에 실패했습니다. ' + (error instanceof Error ? error.message : ''));
    } finally {
      setIsSubmittingTaken(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <SimpleModulePage title="복약" subtitle="(약먹기)" icon="💊" onBack={onBack}>
        <SectionCard>
          <p className="text-[12px] font-semibold text-slate-900 mb-1">오늘 복약 요약(데모)</p>
          <p className="text-[12px] text-slate-600">완료 2/3회 · 최근 3일 평균 86% · 혈압약/혈당약 분리 요약은 모듈 UI에 연결 예정</p>
        </SectionCard>
      </SimpleModulePage>
    );
  }

  if (!todayMeds) {
    return (
      <SimpleModulePage title="복약" subtitle="(약먹기)" icon="💊" onBack={onBack}>
        <SectionCard>
          <p className="text-[12px] font-semibold text-slate-900 mb-1">복약 데이터 로딩 중</p>
          <p className="text-[12px] text-slate-600">약물 정보를 준비하고 있습니다...</p>
        </SectionCard>
      </SimpleModulePage>
    );
  }

  if (!medsData) {
    return (
      <SimpleModulePage title="복약" subtitle="(약먹기)" icon="💊" onBack={onBack}>
        <SectionCard>
          <p className="text-[12px] font-semibold text-slate-900 mb-1">복약 데이터 로딩 중</p>
          <p className="text-[12px] text-slate-600">약물 정보를 준비하고 있습니다...</p>
        </SectionCard>
      </SimpleModulePage>
    );
  }

  const { summary, medications } = medsData;
  
  const selectedDateObj = new Date(selectedDate);
  const dateTitle = isToday 
    ? '오늘 약 복용'
    : selectedDateObj.toLocaleDateString('ko-KR', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
      });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (isToday) {
        await refreshTodayMeds();
      } else {
        const token = localStorage.getItem('authToken');
        if (token) {
          const response = await fetch(`${config.apiUrl}/meds/date/${selectedDate}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setMedsData(data);
            }
          }
        }
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <SimpleModulePage title="복약" subtitle="(약먹기)" icon="💊" onBack={onBack}>
      <SectionCard className="bg-slate-50 border-slate-200">
        <div className="flex items-center gap-2">
          <label className="text-[12px] font-semibold text-slate-700 whitespace-nowrap">날짜 선택:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="flex-1 text-[12px] px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      </SectionCard>

      <SectionCard className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[13px] font-semibold text-slate-900">{dateTitle}</p>
            <p className="text-[11px] text-slate-600 mt-1">{isToday ? '복약 요약' : '해당 일자 복약 요약'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="px-2 py-1 text-[10px] rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-50"
              title="데이터 새로고침"
            >
              {isRefreshing ? '로딩중...' : '🔄'}
            </button>
            <div className="text-right">
              <p className="text-[20px] font-bold text-violet-700">{summary.completionRate}%</p>
              <p className="text-[10px] text-slate-600">
                {summary.totalTaken}/{summary.totalScheduled}회 완료
              </p>
            </div>
          </div>
        </div>

        <div className="w-full bg-white rounded-full h-2 overflow-hidden">
          <div
            className="bg-gradient-to-r from-violet-500 to-purple-500 h-full transition-all duration-300"
            style={{ width: `${summary.completionRate}%` }}
          />
        </div>
      </SectionCard>

      {medications.length > 0 ? (
        medications.map((med: any) => (
          <SectionCard key={med.id} className="border-slate-200">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-xl flex-shrink-0">
                {med.imageUrl ? (
                  <img src={med.imageUrl} alt={med.name} className="w-full h-full rounded-lg object-cover" />
                ) : (
                  <span className="text-[20px]">💊</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-slate-900 truncate">{med.name}</p>
                <p className="text-[11px] text-slate-600 mt-0.5">
                  {med.dosage && <span>{med.dosage}</span>}
                  {med.mealRelation && <span> · {getMealRelationLabel(med.mealRelation)}</span>}
                </p>

                <div className="flex flex-wrap gap-2 mt-2">
                  {med.scheduledTimes.map((schedTime: any, idx: number) => {
                    const isTaken = med.takenCount > 0 && idx < med.takenCount;
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          if (!isTaken) {
                            handleOpenTakenConfirm(med, schedTime);
                          }
                        }}
                        disabled={isTaken}
                        className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                          isTaken
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200 cursor-default'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 cursor-pointer active:bg-slate-200'
                        }`}
                        title={isTaken ? '복용 완료' : '복용 체크'}
                      >
                        {isTaken ? '✓' : ''} {schedTime.period} {schedTime.time}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3">
                  <p className="text-[10px] text-slate-500">
                    {med.takenCount}/{med.scheduledTimes.length}회 복용
                  </p>
                </div>
              </div>
            </div>
          </SectionCard>
        ))
      ) : (
        <SectionCard>
          <p className="text-[12px] font-semibold text-slate-900 mb-1">등록된 약물이 없습니다</p>
          <p className="text-[12px] text-slate-600">복약 스케줄을 등록하면 여기에 표시됩니다.</p>
        </SectionCard>
      )}

      <SectionCard>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-[13px] hover:shadow-lg transition-shadow"
        >
          + 신규 복약정보 등록
        </button>
      </SectionCard>

      {showTakenConfirmModal && selectedMedForConfirm && selectedScheduleTime && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="absolute inset-0" onClick={() => setShowTakenConfirmModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-5 max-w-xs mx-4 space-y-4">
            <h3 className="text-[14px] font-bold text-slate-900">복용 체크</h3>
            <p className="text-[13px] text-slate-700">
              <span className="font-semibold">{selectedMedForConfirm.name}</span>을(를) 복용하셨습니까?
            </p>

            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <p className="text-[11px] text-slate-600">
                <span className="font-semibold">용량:</span> {selectedMedForConfirm.dosage}
              </p>
              <p className="text-[11px] text-slate-600">
                <span className="font-semibold">시간:</span> {selectedScheduleTime.time}
              </p>
              {selectedMedForConfirm.mealRelation && (
                <p className="text-[11px] text-slate-600">
                  <span className="font-semibold">식사:</span> {getMealRelationLabel(selectedMedForConfirm.mealRelation)}
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowTakenConfirmModal(false);
                  setSelectedMedForConfirm(null);
                  setSelectedScheduleTime(null);
                }}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 font-semibold text-[12px] hover:bg-slate-50"
                disabled={isSubmittingTaken}
              >
                취소
              </button>
              <button
                onClick={handleConfirmTaken}
                disabled={isSubmittingTaken}
                className="flex-1 px-3 py-2 rounded-lg bg-yellow-600 text-white font-semibold text-[12px] hover:bg-yellow-700 disabled:opacity-50"
              >
                {isSubmittingTaken ? "저장중..." : "복용 확인"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <MedicationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={async (formData) => {
          setIsSaving(true);
          try {
            const token = localStorage.getItem('authToken');
            if (!token) throw new Error('인증 토큰이 없습니다.');

            const response = await fetch(`${config.apiUrl}/meds/register`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                medication_name: formData.medicationName,
                medication_type: formData.medicationType,
                dosage: formData.dosage,
                dose_amount: formData.doseAmount,
                dose_unit: formData.doseUnit,
                purpose: formData.purpose,
                prescribed_by: formData.prescribedBy,
                start_date: formData.startDate,
                end_date: formData.endDate,
                frequency_type: formData.frequencyType,
                times_per_day: formData.timesPerDay,
                morning_time: formData.frequencyType === 'daily' ? formData.morningTime : null,
                afternoon_time: formData.frequencyType === 'daily' && formData.timesPerDay >= 2 ? formData.afternoonTime : null,
                evening_time: formData.frequencyType === 'daily' && formData.timesPerDay >= 3 ? formData.eveningTime : null,
                night_time: formData.frequencyType === 'daily' && formData.timesPerDay >= 4 ? formData.nightTime : null,
                meal_relation: formData.mealRelation,
                notes: formData.notes,
              }),
            });

            if (response.ok) {
              setIsModalOpen(false);
              if (isToday) {
                await refreshTodayMeds();
              } else {
                const refreshResponse = await fetch(`${config.apiUrl}/meds/date/${selectedDate}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                });
                if (refreshResponse.ok) {
                  const refreshData = await refreshResponse.json();
                  if (refreshData.success) {
                    setMedsData(refreshData);
                  }
                }
              }
              return await response.json();
            } else {
              throw new Error('복약 정보 등록 실패');
            }
          } catch (error) {
            console.error('Failed to register medication:', error);
            throw error;
          } finally {
            setIsSaving(false);
          }
        }}
        isLoading={isSaving}
        selectedDate={selectedDate}
      />
      )}

    </SimpleModulePage>
  );
});

SectionCard.displayName = 'SectionCard';
MedsPage.displayName = 'MedsPage';
